import React, { useState } from 'react';
import { Shield, Eye, EyeOff } from 'lucide-react';
import { apiFetch } from '@/lib/api';

interface PasswordResetFlowProps {
  email: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const PasswordResetFlow: React.FC<PasswordResetFlowProps> = ({ email, onSuccess, onCancel }) => {
  const [resetStep, setResetStep] = useState<'request' | 'verify'>('request');
  const [resetOtp, setResetOtp] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);

  // Timer for resend OTP
  React.useEffect(() => {
    let interval: any;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleRequestReset = async () => {
    setResetLoading(true);
    setResetMessage('');
    try {
      const response = await apiFetch('/api/auth/request-password-otp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to send reset code');
      setResetStep('verify');
      setResetMessage('Security code sent to your email.');
      setResendTimer(60);
    } catch (err: any) {
      setResetMessage('Error: ' + err.message);
    } finally {
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setResetLoading(true);
    setResetMessage('');
    try {
      const response = await apiFetch('/api/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp: resetOtp, password: resetNewPassword })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to reset password');
      setResetMessage('Password reset successfully!');
      onSuccess();
    } catch (err: any) {
      setResetMessage('Error: ' + err.message);
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-3xl border border-cyan-500/10 shadow-xl animate-in fade-in zoom-in duration-300">
      <h3 className="text-xl font-bold text-zinc-900 mb-6 flex items-center gap-2 uppercase tracking-tighter">
        <Shield className="w-5 h-5 text-cyan-600" />
        Reset Password
      </h3>

      {resetStep === 'request' ? (
        <div className="space-y-6">
          <p className="text-zinc-600 text-sm font-medium">
            We will send a 6-digit verification code to <strong className="text-cyan-600 font-black">{email}</strong> to verify your account.
          </p>
          <div className="flex gap-4">
            <button
              onClick={handleRequestReset}
              disabled={resetLoading}
              className="flex-grow py-4 bg-cyan-600 text-white font-black text-xs uppercase tracking-[0.2em] rounded-xl hover:bg-cyan-500 transition-all disabled:opacity-50 shadow-lg shadow-cyan-500/20"
            >
              {resetLoading ? 'Sending...' : 'Send Verification Code'}
            </button>
            <button
              onClick={onCancel}
              className="px-6 py-4 bg-zinc-50 text-zinc-600 font-bold text-xs uppercase tracking-[0.2em] rounded-xl hover:bg-zinc-100 transition-all border border-cyan-500/5"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div>
            <label className="block text-[10px] font-black text-zinc-400 mb-2 uppercase tracking-widest pl-1">
              Verification Code
            </label>
            <input
              type="text"
              value={resetOtp}
              onChange={(e) => setResetOtp(e.target.value)}
              required
              maxLength={6}
              className="block w-full px-4 py-4 bg-zinc-50 border border-cyan-500/5 rounded-xl text-zinc-900 focus:outline-none focus:ring-4 focus:ring-cyan-500/5 transition-all text-center tracking-[1em] font-black text-xl placeholder:text-zinc-200"
              placeholder="000000"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-zinc-400 mb-2 uppercase tracking-widest pl-1">
              New Password
            </label>
            <div className="relative group/input">
              <input
                type={showResetPassword ? "text" : "password"}
                value={resetNewPassword}
                onChange={(e) => setResetNewPassword(e.target.value)}
                required
                className="block w-full px-4 py-4 bg-zinc-50 border border-cyan-500/5 rounded-xl text-zinc-900 focus:outline-none focus:ring-4 focus:ring-cyan-500/5 transition-all pr-12 font-bold placeholder:text-zinc-300"
                placeholder="••••••••"
              />
              <button
                type="button"
                onClick={() => setShowResetPassword(!showResetPassword)}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-zinc-400 hover:text-cyan-600 transition-colors"
              >
                {showResetPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>
          
          {resetMessage && (
            <div className={`p-4 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] text-center ${resetMessage.startsWith('Error') ? 'bg-red-50 text-red-500 border border-red-100' : 'bg-cyan-50 text-cyan-600 border border-cyan-100'}`}>
              {resetMessage}
            </div>
          )}

          <div className="flex gap-4 pt-2">
            <button
              type="submit"
              disabled={resetLoading}
              className="flex-grow py-4 bg-cyan-600 text-white font-black text-xs uppercase tracking-[0.2em] rounded-xl hover:bg-cyan-500 transition-all disabled:opacity-50 shadow-lg shadow-cyan-500/20"
            >
              {resetLoading ? 'Resetting...' : 'Reset Password'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-4 bg-zinc-50 text-zinc-600 font-bold text-xs uppercase tracking-[0.2em] rounded-xl hover:bg-zinc-100 transition-all border border-cyan-500/5"
            >
              Cancel
            </button>
          </div>
          
          <div className="mt-6 flex justify-center">
            <button
              type="button"
              onClick={handleRequestReset}
              disabled={resetLoading || resendTimer > 0}
              className="text-[10px] font-black text-cyan-600 hover:text-cyan-500 transition-colors uppercase tracking-[0.2em] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend Verification Code'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
